/**
 * Operations Page
 * 
 * List and manage operations for an account.
 * 
 * @validates Requirements 8.1, 8.3, 8.4, 8.5 - Operations management
 */

import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useOperationStore } from '../store/operationStore';
import { useAccountStore } from '../store/accountStore';
import { useUserSettingsStore, getDefaultAccountId } from '../store/userSettingsStore';
import { useAuthStore } from '../store/authStore';
import { useCategoryStore } from '../store/categoryStore';
import { usePaymentMethodStore } from '../store/paymentMethodStore';
import { Card, Button, Modal } from '../components/ui';
import { AccountSelector } from '../components/ui/AccountSelector';
import { SearchableSelect } from '../components/common/SearchableSelect';
import { ReconcileModal } from '../components/ReconcileModal';
import { DuplicateModal } from '../components/DuplicateModal';
import { formatCurrency, formatDate } from '../utils/formatters';
import { api } from '../services/api';
import type { Operation, Counterparty, OperationStatus } from '@budgira/shared';
import { InterestCalculator } from '../components/InterestCalculator';

interface OperationLimitStatus {
  limit: number;
  used: number;
  remaining: number;
  canCreate: boolean;
}

// Helper function to get the effective date for sorting/display (accounting date if exists, otherwise payment date)
const getEffectiveDate = (op: Operation): string => {
  return op.accountingDate ?? op.paymentDate;
};

// Helper function to extract YYYY-MM-DD from a date string
const getDateOnly = (dateStr: string): string => {
  return dateStr.split('T')[0] ?? dateStr;
};

// Helper to get today's date as YYYY-MM-DD
const getTodayDateString = (): string => {
  return new Date().toISOString().split('T')[0] ?? '';
};

const OperationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId: urlAccountId } = useParams<{ accountId: string }>();
  const { selectedAccount, selectAccount, accounts, fetchAccounts, balances, fetchBalance } = useAccountStore();
  const { preferences, updatePreferences } = useUserSettingsStore();
  const { user, canManageSchedulers } = useAuthStore();
  const { categories, fetchCategories } = useCategoryStore();
  const { paymentMethods, fetchPaymentMethods } = usePaymentMethodStore();
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const {
    operations,
    total,
    page,
    pageSize,
    totalPages,
    filters,
    isLoading,
    fetchOperations,
    cycleStatus,
    reconcileWithDate,
    deleteOperation,
    deleteOperationsBatch,
    setFilters,
    setPage,
    setPageSize,
    clearFilters,
    initializeFiltersFromPreferences,
    getFiltersForSave,
  } = useOperationStore();

  // Check if any filter is active
  const hasActiveFilters = Boolean(
    filters.startDate || 
    filters.endDate || 
    filters.counterpartyId || 
    filters.categoryId || 
    filters.paymentMethodId || 
    (filters.status && filters.status.length > 0)
  );
  
  const [showFilters, setShowFilters] = useState(false);
  const [showDaySeparators, setShowDaySeparators] = useState(true);
  
  // Track current scroll position continuously for restoration
  const currentScrollY = useRef(0);
  
  useEffect(() => {
    const handleScroll = () => {
      currentScrollY.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Restore page and scroll from URL query params
  const searchParams = new URLSearchParams(location.search);
  const urlPage = searchParams.get('page');
  const urlScrollY = searchParams.get('scrollY');
  const initialPageRestored = useRef(false);
  const initialScrollRestored = useRef(false);
  
  // Debug: log URL params on mount (disabled)
  // useEffect(() => {
  //   console.log('[OperationsPage] Mount - location.search:', location.search, 'urlPage:', urlPage, 'urlScrollY:', urlScrollY);
  // }, []);
  
  // Restore page from URL on mount
  useLayoutEffect(() => {
    if (urlPage && !initialPageRestored.current) {
      const pageNum = parseInt(urlPage, 10);
      if (!isNaN(pageNum) && pageNum >= 1) {
        initialPageRestored.current = true;
        setPage(pageNum);
      }
    }
  }, [urlPage, setPage]);
  
  // Track if page was restored to delay initial fetch
  const [pageReady, setPageReady] = useState(!urlPage);
  
  useEffect(() => {
    if (urlPage && !pageReady) {
      // Give time for setPage to take effect
      const timer = setTimeout(() => setPageReady(true), 50);
      return () => clearTimeout(timer);
    }
  }, [urlPage, pageReady]);
  
  // Restore scroll position after operations are loaded
  useEffect(() => {
    if (urlScrollY && !isLoading && operations.length > 0 && !initialScrollRestored.current) {
      const scrollY = parseInt(urlScrollY, 10);
      if (!isNaN(scrollY) && scrollY >= 0) {
        initialScrollRestored.current = true;
        setTimeout(() => {
          window.scrollTo(0, scrollY);
          const newUrl = location.pathname;
          window.history.replaceState({}, '', newUrl);
        }, 150);
      }
    }
  }, [urlScrollY, isLoading, operations.length, location.pathname]);
  
  // Auto-open filters panel when filters are active
  useEffect(() => {
    setShowFilters(hasActiveFilters);
  }, [hasActiveFilters]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState<string | null>(null);
  const [limitStatus, setLimitStatus] = useState<OperationLimitStatus | null>(null);
  
  // Reconcile modal state
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [operationToReconcile, setOperationToReconcile] = useState<Operation | null>(null);
  
  // Duplicate modal state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [operationToDuplicate, setOperationToDuplicate] = useState<Operation | null>(null);
  
  // Selection state for batch operations
  const [selectedOperationIds, setSelectedOperationIds] = useState<Set<string>>(new Set());
  const [batchDeleteModalOpen, setBatchDeleteModalOpen] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  
  // Balance at date state
  const getEndOfMonth = (): string => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const isoDate = lastDay.toISOString().split('T')[0];
    return isoDate || now.toISOString().split('T')[0] || '';
  };
  
  // Load targetDate from localStorage or use end of month as default
  const [targetDate, setTargetDate] = useState<string>(() => {
    const saved = localStorage.getItem('budgira_targetDate');
    if (saved) return saved;
    return getEndOfMonth();
  });
  
  // Save targetDate to localStorage when it changes
  useEffect(() => {
    if (targetDate) {
      localStorage.setItem('budgira_targetDate', targetDate);
    }
  }, [targetDate]);
  
  const [balanceAtDate, setBalanceAtDate] = useState<number | null>(null);
  const [isLoadingBalanceAtDate, setIsLoadingBalanceAtDate] = useState(false);
  
  // Operations now come with runningBalance from the backend
  // No need to calculate it here - just use op.runningBalance directly
  const operationsWithCumulativeBalance = React.useMemo(() => {
    if (!selectedAccount || operations.length === 0) return [];
    
    // The backend returns runningBalance for each operation
    // We just need to map it to cumulativeBalance for display
    return operations.map((op) => ({
      ...op,
      // runningBalance from backend is the balance AFTER this operation
      // We want to display the balance BEFORE this operation
      // So we need to reverse the operation's effect
      cumulativeBalance: (op as Operation & { runningBalance?: number }).runningBalance !== undefined
        ? (op as Operation & { runningBalance?: number }).runningBalance! - (op.direction === 'credit' ? op.amount : -op.amount)
        : 0,
    }));
  }, [operations, selectedAccount]);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    operation: Operation;
    x: number;
    y: number;
  } | null>(null);
  
  // Clear selection when operations change (e.g., page change, filter change)
  useEffect(() => {
    setSelectedOperationIds(new Set());
  }, [operations]);
  
  // Selection handlers
  const handleToggleSelectAll = () => {
    if (selectedOperationIds.size === operations.length) {
      // All selected, deselect all
      setSelectedOperationIds(new Set());
    } else {
      // Select all visible operations
      setSelectedOperationIds(new Set(operations.map(op => op.id)));
    }
  };
  
  const handleToggleSelect = (operationId: string) => {
    setSelectedOperationIds(prev => {
      const next = new Set(prev);
      if (next.has(operationId)) {
        next.delete(operationId);
      } else {
        next.add(operationId);
      }
      return next;
    });
  };
  
  const isAllSelected = operations.length > 0 && selectedOperationIds.size === operations.length;
  const isSomeSelected = selectedOperationIds.size > 0 && selectedOperationIds.size < operations.length;
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressTriggered = useRef(false);
  const lastUserIdRef = useRef<string | null>(user?.id || null);
  const saveFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    };
    const handleScroll = () => setContextMenu(null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setContextMenu(null);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Long press handlers for mobile
  const handleTouchStart = (event: React.TouchEvent, op: Operation) => {
    longPressTriggered.current = false;
    const touch = event.touches[0];
    if (!touch) return;
    
    const x = Math.min(touch.clientX, window.innerWidth - 200);
    const y = Math.min(touch.clientY, window.innerHeight - 250);
    
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      // Vibrate on mobile if supported
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      setContextMenu({ operation: op, x, y });
    }, 500); // 500ms long press
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchMove = () => {
    // Cancel long press if user moves finger
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Use URL accountId or fallback to selected account's ID
  const accountId = urlAccountId || selectedAccount?.id;

  useEffect(() => {
    if (accounts.length === 0) {
      fetchAccounts();
    }
  }, [accounts.length, fetchAccounts]);

  // Select default account if none selected and no URL param
  useEffect(() => {
    if (!urlAccountId && !selectedAccount && accounts.length > 0) {
      const defaultAccountId = getDefaultAccountId();
      if (defaultAccountId && accounts.some(a => a.id === defaultAccountId)) {
        selectAccount(defaultAccountId);
      } else if (accounts[0]) {
        selectAccount(accounts[0].id);
      }
    }
  }, [urlAccountId, selectedAccount, accounts, selectAccount]);

  // Track the previous account ID to detect account changes
  const prevAccountIdRef = useRef<string | undefined>(urlAccountId);
  
  useEffect(() => {
    if (urlAccountId) {
      selectAccount(urlAccountId);
      // Fetch balance for this account to have projectedBalance
      fetchBalance(urlAccountId);
      // Reset to page 1 ONLY when changing to a different account
      if (prevAccountIdRef.current && prevAccountIdRef.current !== urlAccountId) {
        setPage(1);
      }
      prevAccountIdRef.current = urlAccountId;
    }
  }, [urlAccountId, selectAccount, setPage, fetchBalance]);

  // Sync pageSize with user preferences
  useEffect(() => {
    if (preferences?.operationsPageSize && preferences.operationsPageSize !== pageSize) {
      setPageSize(preferences.operationsPageSize);
    }
  }, [preferences?.operationsPageSize, pageSize, setPageSize]);

  // Initialize filters from user preferences (when user changes or preferences load)
  useEffect(() => {
    const currentUserId = user?.id || null;
    
    // If user changed, reset filters and load new ones
    if (currentUserId !== lastUserIdRef.current) {
      // Clear current filters first
      clearFilters();
      
      // Then load the new user's saved filters
      if (currentUserId && preferences?.operationFilters) {
        initializeFiltersFromPreferences(preferences.operationFilters);
      }
      
      // Update the ref after loading filters
      lastUserIdRef.current = currentUserId;
    }
  }, [user?.id, preferences?.operationFilters, clearFilters, initializeFiltersFromPreferences]);

  // Save filters to user preferences when they change (debounced)
  useEffect(() => {
    // Skip if no user logged in or user just changed (filters are being initialized)
    if (!user?.id || lastUserIdRef.current !== user.id) return;
    
    // Clear previous timeout
    if (saveFiltersTimeoutRef.current) {
      clearTimeout(saveFiltersTimeoutRef.current);
    }
    
    // Debounce the save to avoid too many API calls
    saveFiltersTimeoutRef.current = setTimeout(() => {
      const filtersToSave = getFiltersForSave();
      // Only save if there are actual filter values or we need to clear them
      const hasFilters = Object.keys(filtersToSave).length > 0;
      const hadFilters = preferences?.operationFilters && Object.keys(preferences.operationFilters).length > 0;
      
      if (hasFilters || hadFilters) {
        updatePreferences({ 
          operationFilters: hasFilters ? filtersToSave : null 
        });
      }
    }, 1000); // Wait 1 second before saving
    
    return () => {
      if (saveFiltersTimeoutRef.current) {
        clearTimeout(saveFiltersTimeoutRef.current);
      }
    };
  }, [user?.id, filters.status, filters.categoryId, filters.counterpartyId, filters.paymentMethodId, filters.startDate, filters.endDate, filters.direction, filters.search, getFiltersForSave, updatePreferences, preferences?.operationFilters]);

  // Fetch filter options (categories, counterparties, payment methods)
  useEffect(() => {
    fetchCategories();
    fetchPaymentMethods();
    
    // Fetch counterparties
    const loadCounterparties = async () => {
      try {
        const response = await api.get<{ success: boolean; data: Counterparty[] }>('/counterparties');
        if (response.data.success) {
          setCounterparties(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch counterparties:', error);
      }
    };
    loadCounterparties();
  }, [fetchCategories, fetchPaymentMethods]);

  useEffect(() => {
    // Wait for page restoration to complete before fetching
    if (accountId && pageReady) {
      fetchOperations(accountId);
    }
  }, [accountId, page, pageSize, filters, fetchOperations, pageReady]);

  // Fetch operation limit status for free accounts
  useEffect(() => {
    const fetchLimitStatus = async () => {
      if (!accountId || user?.role !== 'free') {
        setLimitStatus(null);
        return;
      }
      try {
        const response = await api.get<{ success: boolean; data: OperationLimitStatus }>(
          `/accounts/${accountId}/operations/limit-status`
        );
        if (response.data.success) {
          setLimitStatus(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch operation limit status:', error);
      }
    };
    fetchLimitStatus();
  }, [accountId, user?.role, operations.length]); // Re-fetch when operations change

  // Fetch balance at target date
  useEffect(() => {
    const fetchBalanceAtDate = async () => {
      if (!accountId || !targetDate) {
        setBalanceAtDate(null);
        return;
      }
      setIsLoadingBalanceAtDate(true);
      try {
        const response = await api.get<{ success: boolean; data: { balance: number; targetDate: string } }>(
          `/accounts/${accountId}/balance-at-date?date=${targetDate}`
        );
        if (response.data.success) {
          setBalanceAtDate(response.data.data.balance);
        }
      } catch (error) {
        console.error('Failed to fetch balance at date:', error);
        setBalanceAtDate(null);
      } finally {
        setIsLoadingBalanceAtDate(false);
      }
    };
    fetchBalanceAtDate();
  }, [accountId, targetDate, operations]); // Re-fetch when operations change

  const handleAccountChange = (newAccountId: string) => {
    navigate(`/accounts/${newAccountId}/operations`);
  };

  const handleDeleteClick = (id: string) => {
    setOperationToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (operationToDelete) {
      await deleteOperation(operationToDelete);
      setDeleteModalOpen(false);
      setOperationToDelete(null);
      // Refresh operations after deletion
      if (accountId) {
        fetchOperations(accountId);
      }
    }
  };

  // Batch delete handler
  const handleBatchDeleteConfirm = async () => {
    if (selectedOperationIds.size === 0) return;
    
    setIsBatchDeleting(true);
    try {
      await deleteOperationsBatch(Array.from(selectedOperationIds));
      setBatchDeleteModalOpen(false);
      setSelectedOperationIds(new Set());
      // Refresh operations after deletion
      if (accountId) {
        fetchOperations(accountId);
      }
      // Refresh accounts to update balance
      fetchAccounts();
    } catch (error) {
      console.error('Failed to delete operations batch:', error);
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const handleCycleStatus = async (id: string, direction: 'forward' | 'backward' = 'forward', operation?: Operation) => {
    // If going forward and operation is pointed, open reconcile modal instead
    if (direction === 'forward' && operation?.status === 'pointed') {
      setOperationToReconcile(operation);
      setReconcileModalOpen(true);
      setContextMenu(null);
      return;
    }
    
    await cycleStatus(id, direction);
    // Refresh operations to get updated state
    if (accountId) {
      fetchOperations(accountId);
      // Refresh balance for current account
      fetchBalance(accountId);
    }
    // Refresh accounts to update balance
    fetchAccounts();
    setContextMenu(null);
  };

  const handleReconcileConfirm = async (accountingDate: string) => {
    if (!operationToReconcile) return;
    
    await reconcileWithDate(operationToReconcile.id, accountingDate);
    
    // Refresh operations to get updated state
    if (accountId) {
      fetchOperations(accountId);
      // Refresh balance for current account
      fetchBalance(accountId);
    }
    // Refresh accounts to update balance
    fetchAccounts();
    
    setReconcileModalOpen(false);
    setOperationToReconcile(null);
  };

  // Handle right-click context menu
  const handleContextMenu = (event: React.MouseEvent, op: Operation) => {
    event.preventDefault();
    
    // Calculate position, keeping menu within viewport
    const x = Math.min(event.clientX, window.innerWidth - 200);
    const y = Math.min(event.clientY, window.innerHeight - 250);
    
    setContextMenu({ operation: op, x, y });
  };

  const handleContextMenuEdit = () => {
    if (contextMenu && accountId) {
      const scrollY = currentScrollY.current;
      navigate(`/operations/${contextMenu.operation.id}/edit`, {
        state: { 
          returnTo: `/accounts/${accountId}/operations?page=${page}&scrollY=${scrollY}`,
        }
      });
      setContextMenu(null);
    }
  };

  const handleContextMenuDelete = () => {
    if (contextMenu) {
      handleDeleteClick(contextMenu.operation.id);
      setContextMenu(null);
    }
  };

  const handleContextMenuDuplicate = () => {
    if (contextMenu) {
      setOperationToDuplicate(contextMenu.operation);
      setDuplicateModalOpen(true);
      setContextMenu(null);
    }
  };
  
  const handleDuplicateConfirm = async (paymentDate: string, amount: number) => {
    if (operationToDuplicate && accountId) {
      try {
        await api.post(`/operations/${operationToDuplicate.id}/duplicate`, { paymentDate, amount });
        fetchOperations(accountId);
        setDuplicateModalOpen(false);
        setOperationToDuplicate(null);
      } catch (error) {
        console.error('Failed to duplicate operation:', error);
      }
    }
  };

  const handleContextMenuToScheduler = () => {
    if (contextMenu && accountId) {
      const op = contextMenu.operation;
      // Navigate to scheduler form with operation data pre-filled
      navigate(`/accounts/${accountId}/schedulers/new`, {
        state: {
          fromOperation: {
            direction: op.direction,
            amount: op.amount,
            counterpartyName: op.counterparty?.name || '',
            categoryId: op.categories?.[0]?.categoryId || '',
            categoryName: op.categories?.[0]?.category?.name || '',
            paymentMethodId: op.paymentMethodId || '',
            paymentDetails: op.paymentDetails || '',
            reference: op.reference || '',
            comment: op.comment || '',
          }
        }
      });
      setContextMenu(null);
    }
  };

  if (!accountId || !selectedAccount) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">{t('operations.title')}</h1>
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-500">{t('operations.selectAccount')}</p>
            <Link to="/accounts" className="mt-4 inline-block">
              <Button>{t('operations.goToAccounts')}</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Check if user can modify operations on this account
  const canWrite = !selectedAccount.isShared || selectedAccount.permission === 'write' || selectedAccount.permission === 'owner';

  return (
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
      {/* Header with Account Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Account Selector */}
          <AccountSelector
            accounts={accounts}
            selectedAccountId={accountId}
            onAccountChange={handleAccountChange}
          />
          <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">{t('operations.countLabel', { count: total })}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer" title={t('operations.daySeparatorsTooltip', 'Afficher les séparateurs entre chaque jour')}>
            <button
              type="button"
              role="switch"
              aria-checked={showDaySeparators}
              aria-label={t('operations.daySeparators', 'Séparateurs de jours')}
              onClick={() => setShowDaySeparators(!showDaySeparators)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showDaySeparators ? 'bg-violet-600' : 'bg-slate-300 dark:bg-slate-600'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${showDaySeparators ? 'translate-x-[18px]' : 'translate-x-1'}`} />
            </button>
            <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline">{t('operations.daySeparators', 'Séparateurs')}</span>
          </label>
          <Button 
            variant={showFilters ? "outline" : "primary"} 
            size="sm" 
            onClick={() => setShowFilters(!showFilters)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="hidden sm:inline ml-1">{t('operations.filters')}</span>
            {hasActiveFilters && (
              <span className="ml-1.5 w-2 h-2 rounded-full bg-current inline-block"></span>
            )}
          </Button>
          {canWrite && (!limitStatus || limitStatus.canCreate) && (
            <Link to={`/accounts/${accountId}/operations/new`}>
              <Button size="sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline ml-1">{t('operations.add')}</span>
              </Button>
            </Link>
          )}
          <Link to={`/accounts/${accountId}/operations/export`}>
            <Button variant="outline" size="sm" title={t('export.title')}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="hidden sm:inline ml-1">{t('common.export')}</span>
            </Button>
          </Link>
          {canWrite && (
            <Link to={`/accounts/${accountId}/operations/import`}>
              <Button variant="outline" size="sm" title={t('import.title')}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m4-8l-4-4m0 0L16 8m-4-4v12" />
                </svg>
                <span className="hidden sm:inline ml-1">{t('common.import')}</span>
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Operation limit warning for free accounts */}
      {limitStatus && limitStatus.limit > 0 && (
        <div className={`rounded-lg px-4 py-3 ${limitStatus.canCreate ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-2">
            <svg className={`w-5 h-5 ${limitStatus.canCreate ? 'text-amber-500' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className={`text-sm ${limitStatus.canCreate ? 'text-amber-700' : 'text-red-700'}`}>
              {limitStatus.canCreate
                ? t('operations.limitWarning', { used: limitStatus.used, limit: limitStatus.limit, remaining: limitStatus.remaining })
                : t('operations.limitReached', { limit: limitStatus.limit })}
            </span>
          </div>
        </div>
      )}

      {/* Balance display */}
      <div className="flex gap-2 sm:gap-3 flex-col sm:flex-row">
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 py-2 sm:py-4 px-4 sm:px-6">
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('accounts.pointed', 'Solde pointé')}</p>
          <p className={`text-lg sm:text-xl font-bold ${(selectedAccount ? (balances[selectedAccount.id]?.balance ?? 0) : 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(selectedAccount ? (balances[selectedAccount.id]?.balance ?? 0) : 0, selectedAccount?.currency || 'EUR')}
          </p>
        </div>
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 py-2 sm:py-4 px-4 sm:px-6">
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('accounts.reconciled', 'Solde rapproché')}</p>
          <p className={`text-lg sm:text-xl font-bold ${(selectedAccount ? (balances[selectedAccount.id]?.reconciledBalance ?? 0) : 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
            {formatCurrency(selectedAccount ? (balances[selectedAccount.id]?.reconciledBalance ?? 0) : 0, selectedAccount?.currency || 'EUR')}
          </p>
        </div>
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 py-2 sm:py-4 px-4 sm:px-6">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('accounts.balanceAtDate', 'Solde au')}</p>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="px-2 py-0.5 border border-slate-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          {isLoadingBalanceAtDate ? (
            <p className="text-lg text-slate-400">...</p>
          ) : (
            <p className={`text-lg sm:text-xl font-bold ${(balanceAtDate ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {balanceAtDate !== null ? formatCurrency(balanceAtDate, selectedAccount?.currency || 'EUR') : '-'}
            </p>
          )}
        </div>
      </div>

      {/* Interest Calculator - only for accounts with interest configured */}
      {selectedAccount && selectedAccount.interestCalculationMethod && selectedAccount.interestPaymentFrequency && (
        <InterestCalculator
          account={selectedAccount}
          onOperationCreated={() => fetchOperations(accountId)}
        />
      )}

      {/* Filters - Compact layout */}
      {showFilters && (
        <Card className="py-3 px-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
            {/* Date filters */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                {t('operations.filter.startDate', 'Date début')}
              </label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => setFilters({ startDate: e.target.value || undefined })}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                {t('operations.filter.endDate', 'Date fin')}
              </label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => setFilters({ endDate: e.target.value || undefined })}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
              />
            </div>
            
            {/* Counterparty filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                {t('operations.filter.counterparty', 'Tiers')}
              </label>
              <SearchableSelect
                options={counterparties.map((cp) => ({ id: cp.id, label: cp.name }))}
                value={filters.counterpartyId}
                onChange={(value) => setFilters({ counterpartyId: value })}
                placeholder={t('operations.filter.all', 'Tous')}
                allOptionLabel={t('operations.filter.all', 'Tous')}
              />
            </div>
            
            {/* Category filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                {t('operations.filter.category', 'Catégorie')}
              </label>
              <SearchableSelect
                options={categories.flatMap((cat) => [
                  { id: cat.id, label: cat.name },
                  ...(cat.children || []).flatMap((child) => [
                    { id: child.id, label: child.name, sublabel: cat.name },
                    ...(child.children || []).map((grandchild) => ({
                      id: grandchild.id,
                      label: grandchild.name,
                      sublabel: `${cat.name} › ${child.name}`
                    }))
                  ])
                ])}
                value={filters.categoryId}
                onChange={(value) => setFilters({ categoryId: value })}
                placeholder={t('operations.filter.all', 'Tous')}
                allOptionLabel={t('operations.filter.all', 'Tous')}
              />
            </div>
            
            {/* Payment method filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                {t('operations.filter.paymentMethod', 'Mode de paiement')}
              </label>
              <SearchableSelect
                options={paymentMethods.map((pm) => ({ id: pm.id, label: pm.name }))}
                value={filters.paymentMethodId}
                onChange={(value) => setFilters({ paymentMethodId: value })}
                placeholder={t('operations.filter.all', 'Tous')}
                allOptionLabel={t('operations.filter.all', 'Tous')}
              />
            </div>
            
            {/* Status filter (multi-select) */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                {t('operations.filter.status', 'Statut')}
              </label>
              <div className="flex flex-wrap gap-1">
                {(['unpointed', 'pointed', 'reconciled'] as OperationStatus[]).map((statusOption) => {
                  const isSelected = filters.status?.includes(statusOption);
                  const labels: Record<OperationStatus, string> = {
                    unpointed: t('operations.status.unpointed', 'Non pointé'),
                    pointed: t('operations.status.pointed', 'Pointé'),
                    reconciled: t('operations.status.reconciled', 'Rapproché'),
                  };
                  const colors: Record<OperationStatus, string> = {
                    unpointed: isSelected ? 'bg-slate-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
                    pointed: isSelected ? 'bg-green-500 text-white' : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
                    reconciled: isSelected ? 'bg-blue-500 text-white' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
                  };
                  return (
                    <button
                      key={statusOption}
                      type="button"
                      onClick={() => {
                        const currentStatus = filters.status || [];
                        const newStatus = isSelected
                          ? currentStatus.filter((s) => s !== statusOption)
                          : [...currentStatus, statusOption];
                        setFilters({ status: newStatus.length > 0 ? newStatus : undefined });
                      }}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors ${colors[statusOption]}`}
                    >
                      {labels[statusOption]}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Clear filters button - hidden on mobile, shown in grid on larger screens */}
            <div className="hidden xl:flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded transition-colors"
              >
                {t('operations.filter.clear', 'Effacer')}
              </button>
            </div>
          </div>
          
          {/* Clear filters button - mobile/tablet only, centered */}
          <div className="xl:hidden flex justify-center mt-3">
            <button
              onClick={clearFilters}
              className="px-6 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded transition-colors"
            >
              {t('operations.filter.clear', 'Effacer')}
            </button>
          </div>
        </Card>
      )}

      {/* Batch Actions Bar - shown when operations are selected */}
      {selectedOperationIds.size > 0 && canWrite && (
        <div className="sticky top-0 z-10 bg-violet-600 text-white rounded-lg shadow-lg px-4 py-3 flex items-center justify-between animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-3">
            <span className="font-medium">
              {selectedOperationIds.size} {t('operations.selection.selected', 'sélectionnée(s)')}
            </span>
            <button
              onClick={() => setSelectedOperationIds(new Set())}
              className="text-violet-200 hover:text-white transition-colors text-sm underline"
            >
              {t('operations.selection.cancel', 'Annuler')}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBatchDeleteModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {t('operations.selection.delete', 'Supprimer')}
            </button>
          </div>
        </div>
      )}

      {/* Operations Table */}
      <Card>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : operations.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">{t('operations.empty.title')}</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">{t('operations.empty.message')}</p>
            {canWrite && (
              <Link to={`/accounts/${accountId}/operations/new`}>
                <Button>{t('operations.empty.create')}</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-hidden">
            {/* Desktop table */}
            <table className="hidden sm:table w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                  {canWrite && (
                    <th className="w-10 py-3 px-2">
                      <div className="flex items-center justify-center">
                        <button
                          onClick={handleToggleSelectAll}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            isAllSelected
                              ? 'bg-violet-600 border-violet-600 text-white'
                              : isSomeSelected
                                ? 'bg-violet-100 border-violet-400 dark:bg-violet-900/30 dark:border-violet-500'
                                : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500 hover:border-violet-400'
                          }`}
                          title={isAllSelected ? t('operations.selection.deselectAll', 'Tout désélectionner') : t('operations.selection.selectAll', 'Tout sélectionner')}
                        >
                          {isAllSelected && (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {isSomeSelected && !isAllSelected && (
                            <svg className="w-3 h-3 text-violet-600 dark:text-violet-400" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="5" y="11" width="14" height="2" rx="1" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </th>
                  )}
                  <th className="text-left py-3 px-3 text-sm font-medium text-slate-600 dark:text-slate-200">
                    <div>{t('operations.table.sortDate', 'Date de tri')}</div>
                    <div className="text-xs font-normal text-slate-400">{t('operations.table.secondaryDate', 'Date secondaire')}</div>
                  </th>
                  <th className="text-left py-3 px-3 text-sm font-medium text-slate-600 dark:text-slate-200">{t('operations.table.description', 'Tiers/Description')}</th>
                  <th className="hidden md:table-cell text-left py-3 px-3 text-sm font-medium text-slate-600 dark:text-slate-200">{t('operations.table.category')}</th>
                  <th className="hidden lg:table-cell text-left py-3 px-3 text-sm font-medium text-slate-600 dark:text-slate-200">{t('operations.table.paymentMethod')}</th>
                  <th className="hidden xl:table-cell text-left py-3 px-3 text-sm font-medium text-slate-600 dark:text-slate-200">{t('operations.table.paymentDetails')}</th>
                  <th className="text-right py-3 px-3 text-sm font-medium text-slate-600 dark:text-slate-200 whitespace-nowrap">{t('operations.table.amount')}</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-slate-600 dark:text-slate-200 w-16">{t('operations.table.pointed')}</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-slate-600 dark:text-slate-200 w-20">{t('operations.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {operationsWithCumulativeBalance.map((op, index, array) => {
                  const today = getTodayDateString();
                  const currentDate = getDateOnly(getEffectiveDate(op));
                  const prevOp = index > 0 ? array[index - 1] : null;
                  const prevDate = prevOp ? getDateOnly(getEffectiveDate(prevOp)) : null;
                  
                  // Check if we need a day separator (different day from previous) - only if toggle is enabled
                  const needsSeparator = showDaySeparators && prevDate && prevDate !== currentDate;
                  // Check if this is the "today" separator - red line between future and past/today
                  // Since list is sorted DESC (newest first): prevDate is MORE recent than currentDate
                  // Red line when: previous row is future (> today) AND current row is past/today (<= today)
                  const isTodaySeparator = needsSeparator && prevDate > today && currentDate <= today;
                  
                  return (
                  <tr 
                    key={op.id} 
                    className={`border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 select-none ${
                      selectedOperationIds.has(op.id) ? 'bg-violet-50 dark:bg-violet-900/20' : ''
                    } ${needsSeparator ? (isTodaySeparator ? 'border-t-2 border-t-red-200 dark:border-t-red-400/40' : 'border-t-2 border-t-slate-300 dark:border-t-slate-500') : ''}`}
                    onContextMenu={(e) => canWrite && handleContextMenu(e, op)}
                  >
                    {canWrite && (
                      <td className="py-3 px-2">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSelect(op.id);
                            }}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              selectedOperationIds.has(op.id)
                                ? 'bg-violet-600 border-violet-600 text-white'
                                : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500 hover:border-violet-400'
                            }`}
                          >
                            {selectedOperationIds.has(op.id) && (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="py-3 px-3">
                      <div>
                        {op.accountingDate ? (
                          <>
                            {/* When accounting date exists, show it first (bold) as it's the sort date */}
                            <span className="text-sm font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">{formatDate(op.accountingDate)}</span>
                            <div className="text-xs text-slate-400 whitespace-nowrap">{formatDate(op.paymentDate)}</div>
                          </>
                        ) : (
                          <>
                            {/* No accounting date, show payment date as bold (it's the sort date) */}
                            <span className="text-sm font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">{formatDate(op.paymentDate)}</span>
                            <div className="text-xs text-slate-400 whitespace-nowrap">—</div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="max-w-[200px]">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{op.counterparty?.name || '—'}</p>
                        {op.comment && <p className="text-xs text-slate-400 truncate">{op.comment}</p>}
                      </div>
                    </td>
                    <td className="hidden md:table-cell py-3 px-3">
                      {op.categories && op.categories.length > 0 && op.categories[0]?.category?.name ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                          {op.categories[0].category.logoUrl && (
                            <span className="mr-1">{op.categories[0].category.logoUrl}</span>
                          )}
                          {op.categories[0].category.name}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="hidden lg:table-cell py-3 px-3">
                      {op.paymentMethod?.name ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          {op.paymentMethod.logoUrl && (
                            <span className="mr-1">{op.paymentMethod.logoUrl}</span>
                          )}
                          {op.paymentMethod.name}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="hidden xl:table-cell py-3 px-3">
                      <span className="text-sm text-slate-600 dark:text-slate-400">{op.paymentDetails || '—'}</span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div>
                        <span className={`text-sm font-medium whitespace-nowrap ${op.direction === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {op.direction === 'credit' ? '+' : '-'}
                          {formatCurrency(op.amount, selectedAccount.currency)}
                        </span>
                        <div className={`text-[10px] whitespace-nowrap ${op.cumulativeBalance >= 0 ? 'text-slate-400' : 'text-red-400'}`}>
                          {formatCurrency(op.cumulativeBalance, selectedAccount.currency)}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center justify-center">
                        {new Date(op.paymentDate) <= new Date() ? (
                          // Past or today: show status cycle button
                          canWrite ? (
                            <button
                              onClick={() => {
                                handleCycleStatus(op.id, 'forward', op);
                              }}
                              className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 border-2 ${
                                op.status === 'reconciled'
                                  ? 'bg-blue-500 border-blue-500 text-white shadow-sm shadow-blue-200'
                                  : op.status === 'pointed'
                                    ? 'bg-green-500 border-green-500 text-white shadow-sm shadow-green-200'
                                    : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-green-400'
                              }`}
                              title={op.status === 'reconciled' 
                                ? t('operations.status.reconciled', 'Rapproché')
                                : op.status === 'pointed'
                                  ? t('operations.status.pointed', 'Pointé')
                                  : t('operations.status.unpointed', 'Non pointé')}
                            >
                              {op.status === 'reconciled' && (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7M5 6l4 4L19 0" />
                                </svg>
                              )}
                              {op.status === 'pointed' && (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                          ) : (
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                              op.status === 'reconciled'
                                ? 'bg-blue-500 border-blue-500 text-white'
                                : op.status === 'pointed'
                                  ? 'bg-green-500 border-green-500 text-white'
                                  : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                            }`}>
                              {op.status === 'reconciled' && (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7M5 6l4 4L19 0" />
                                </svg>
                              )}
                              {op.status === 'pointed' && (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                          )
                        ) : (
                          // Future: show different icon based on status
                          op.status !== 'unpointed' ? (
                            // Future + pointed/reconciled: faded icon (will be counted when date arrives)
                            <span 
                              className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                                op.status === 'reconciled'
                                  ? 'border-blue-300 dark:border-blue-700 bg-blue-100 dark:bg-blue-900/30 text-blue-400 dark:text-blue-600'
                                  : 'border-green-300 dark:border-green-700 bg-green-100 dark:bg-green-900/30 text-green-400 dark:text-green-600'
                              }`}
                              title={t('operations.futurePointed', 'Sera comptabilisée automatiquement')}
                            >
                              {op.status === 'reconciled' ? (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7M5 6l4 4L19 0" />
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                          ) : (
                            // Future + not pointed: clock icon
                            <span className="w-6 h-6 rounded-full flex items-center justify-center border-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-300 dark:text-slate-500" title={t('operations.future', 'Opération future')}>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {canWrite ? (
                          <>
                            <button 
                              onClick={() => {
                                const scrollY = currentScrollY.current;
                                navigate(`/operations/${op.id}/edit`, {
                                  state: { 
                                    returnTo: `/accounts/${accountId}/operations?page=${page}&scrollY=${scrollY}`,
                                  }
                                });
                              }}
                              className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button onClick={() => handleDeleteClick(op.id)} className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-slate-500 italic">{t('operations.readOnly')}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile list - card layout */}
            <div className="sm:hidden overflow-x-hidden">
              {/* Mobile header with select all */}
              <div className="flex items-center justify-between py-2 px-1 border-b border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  {canWrite && (
                    <button
                      onClick={handleToggleSelectAll}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        isAllSelected
                          ? 'bg-violet-600 border-violet-600 text-white'
                          : isSomeSelected
                            ? 'bg-violet-100 border-violet-400 dark:bg-violet-900/30 dark:border-violet-500'
                            : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500'
                      }`}
                    >
                      {isAllSelected && (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {isSomeSelected && !isAllSelected && (
                        <svg className="w-3 h-3 text-violet-600 dark:text-violet-400" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="5" y="11" width="14" height="2" rx="1" />
                        </svg>
                      )}
                    </button>
                  )}
                  <span>{t('operations.mobileHeader.description', 'Tiers')}</span>
                </div>
                <span>{t('operations.mobileHeader.amount', 'Montant')}</span>
              </div>
              <div>
                {operationsWithCumulativeBalance.map((op, index, array) => {
                  const today = getTodayDateString();
                  const currentDate = getDateOnly(getEffectiveDate(op));
                  const prevOp = index > 0 ? array[index - 1] : null;
                  const prevDate = prevOp ? getDateOnly(getEffectiveDate(prevOp)) : null;
                  
                  // Check if we need a day separator (different day from previous) - only if toggle is enabled
                  const needsSeparator = showDaySeparators && prevDate && prevDate !== currentDate;
                  // Check if this is the "today" separator - red line between future and past/today
                  // Since list is sorted DESC (newest first): prevDate is MORE recent than currentDate
                  // Red line when: previous row is future (> today) AND current row is past/today (<= today)
                  const isTodaySeparator = needsSeparator && prevDate > today && currentDate <= today;
                  
                  // Border class: day separator (thicker) or normal divider
                  const borderClass = needsSeparator 
                    ? (isTodaySeparator ? 'border-t-2 border-t-red-200 dark:border-t-red-400/40' : 'border-t-2 border-t-slate-300 dark:border-t-slate-500')
                    : (index > 0 ? 'border-t border-t-slate-100 dark:border-t-slate-700' : '');
                  
                  return (
                  <div 
                    key={op.id} 
                    className={`py-3 select-none overflow-hidden ${selectedOperationIds.has(op.id) ? 'bg-violet-50 dark:bg-violet-900/20' : ''} ${borderClass}`}
                    onTouchStart={(e) => canWrite && handleTouchStart(e, op)}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={handleTouchMove}
                  >
                    <div className="flex items-start justify-between gap-2 overflow-hidden">
                      {/* Left: checkbox, counterparty, date, description */}
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        {canWrite && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSelect(op.id);
                            }}
                            className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all mt-0.5 ${
                              selectedOperationIds.has(op.id)
                                ? 'bg-violet-600 border-violet-600 text-white'
                                : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500'
                            }`}
                          >
                            {selectedOperationIds.has(op.id) && (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        )}
                        <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{op.counterparty?.name || '—'}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{formatDate(op.paymentDate)}</p>
                        {op.comment && <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{op.comment}</p>}
                        </div>
                      </div>
                      {/* Right: amount, pointed, actions */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className="text-right whitespace-nowrap">
                          <span className={`text-sm font-medium ${op.direction === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {op.direction === 'credit' ? '+' : '-'}{formatCurrency(op.amount, selectedAccount.currency)}
                          </span>
                          <div className={`text-[9px] ${op.cumulativeBalance >= 0 ? 'text-slate-400' : 'text-red-400'}`}>
                            {formatCurrency(op.cumulativeBalance, selectedAccount.currency)}
                          </div>
                        </div>
                        {new Date(op.paymentDate) <= new Date() ? (
                          canWrite ? (
                            <button
                              onClick={() => handleCycleStatus(op.id, 'forward', op)}
                              className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                                op.status === 'reconciled'
                                  ? 'bg-blue-500 border-blue-500 text-white'
                                  : op.status === 'pointed'
                                    ? 'bg-green-500 border-green-500 text-white'
                                    : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                              }`}
                            >
                              {op.status === 'reconciled' && (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7M5 6l4 4L19 0" />
                                </svg>
                              )}
                              {op.status === 'pointed' && (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                          ) : (
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                              op.status === 'reconciled'
                                ? 'bg-blue-500 border-blue-500 text-white'
                                : op.status === 'pointed'
                                  ? 'bg-green-500 border-green-500 text-white'
                                  : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                            }`}>
                              {op.status === 'reconciled' && (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7M5 6l4 4L19 0" />
                                </svg>
                              )}
                              {op.status === 'pointed' && (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                          )
                        ) : (
                          // Future operation
                          op.status !== 'unpointed' ? (
                            // Future + pointed/reconciled: faded icon
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                              op.status === 'reconciled'
                                ? 'border-blue-300 dark:border-blue-700 bg-blue-100 dark:bg-blue-900/30 text-blue-400 dark:text-blue-600'
                                : 'border-green-300 dark:border-green-700 bg-green-100 dark:bg-green-900/30 text-green-400 dark:text-green-600'
                            }`}>
                              {op.status === 'reconciled' ? (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7M5 6l4 4L19 0" />
                                </svg>
                              ) : (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                          ) : (
                            // Future + not pointed: clock icon
                            <span className="w-5 h-5 rounded-full flex items-center justify-center border-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-300 dark:text-slate-500">
                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </span>
                          )
                        )}
                        {canWrite && (
                          <>
                            <button 
                              onClick={() => navigate(`/operations/${op.id}/edit`, {
                                state: { 
                                  returnTo: `/accounts/${accountId}/operations?page=${page}&scrollY=${currentScrollY.current}`,
                                }
                              })}
                              className="p-1 text-slate-400 dark:text-slate-500 hover:text-violet-600 dark:hover:text-violet-400"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button 
                              onClick={() => handleDeleteClick(op.id)}
                              className="p-1 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Category on its own line for better readability on mobile */}
                    {op.categories && op.categories.length > 0 && op.categories[0]?.category?.name && (
                      <div className={`mt-1 ${canWrite ? 'ml-7' : ''}`}>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                          {op.categories[0].category.logoUrl && <span className="mr-1">{op.categories[0].category.logoUrl}</span>}
                          {op.categories[0].category.name}
                        </span>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
            {/* First page button - only show when more than 2 pages */}
            {totalPages > 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1}
                title={t('common.first')}
                className="px-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-2 sm:px-3"
            >
              <span className="hidden sm:inline">{t('common.previous')}</span>
              <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap px-1">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="px-2 sm:px-3"
            >
              <span className="hidden sm:inline">{t('common.next')}</span>
              <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
            {/* Last page button - only show when more than 2 pages */}
            {totalPages > 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                title={t('common.last')}
                className="px-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Context Menu (right-click) */}
      {contextMenu && canWrite && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-[100] min-w-[180px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {/* Edit */}
          <button
            onClick={handleContextMenuEdit}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('common.edit')}
          </button>

          {/* Cycle status - only for past or current operations */}
          {new Date(contextMenu.operation.paymentDate) <= new Date() && (
            <>
              {/* Forward: advance status */}
              {contextMenu.operation.status !== 'reconciled' && (
                <button
                  onClick={() => handleCycleStatus(contextMenu.operation.id, 'forward', contextMenu.operation)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  {contextMenu.operation.status === 'unpointed' ? (
                    <>
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('operations.actions.point', 'Pointer')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7M5 6l4 4L19 0" />
                      </svg>
                      {t('operations.actions.reconcile', 'Rapprocher')}
                    </>
                  )}
                </button>
              )}
              {/* Backward: revert status */}
              {contextMenu.operation.status !== 'unpointed' && (
                <button
                  onClick={() => handleCycleStatus(contextMenu.operation.id, 'backward')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  {contextMenu.operation.status === 'reconciled' ? (
                    <>
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('operations.actions.unreconcile', 'Revenir à Pointé')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {t('operations.actions.unpoint', 'Dépointer')}
                    </>
                  )}
                </button>
              )}
            </>
          )}

          {/* Duplicate - only show if user can create more operations */}
          {(!limitStatus || limitStatus.canCreate) && (
            <button
              onClick={handleContextMenuDuplicate}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {t('operations.actions.duplicate')}
            </button>
          )}

          {/* Convert to Scheduler - only show for premium users */}
          {canManageSchedulers() && (
            <button
              onClick={handleContextMenuToScheduler}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('operations.actions.toScheduler')}
            </button>
          )}

          {/* Separator */}
          <div className="border-t border-slate-100 dark:border-slate-700 my-1" />

          {/* Delete */}
          <button
            onClick={handleContextMenuDelete}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {t('common.delete')}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t('operations.deleteModal.title')}
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-slate-600 dark:text-slate-300">{t('operations.deleteModal.message')}</p>
      </Modal>

      {/* Batch Delete Confirmation Modal */}
      <Modal
        isOpen={batchDeleteModalOpen}
        onClose={() => !isBatchDeleting && setBatchDeleteModalOpen(false)}
        title={t('operations.batchDeleteModal.title', 'Supprimer les opérations')}
        footer={
          <>
            <Button 
              variant="outline" 
              onClick={() => setBatchDeleteModalOpen(false)}
              disabled={isBatchDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleBatchDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isBatchDeleting}
            >
              {isBatchDeleting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('common.deleting', 'Suppression...')}
                </span>
              ) : (
                t('common.delete')
              )}
            </Button>
          </>
        }
      >
        <p className="text-slate-600 dark:text-slate-300">
          {t('operations.batchDeleteModal.message', {
            count: selectedOperationIds.size,
            defaultValue: `Êtes-vous sûr de vouloir supprimer ${selectedOperationIds.size} opération(s) ? Cette action est irréversible.`
          })}
        </p>
      </Modal>

      {/* Reconcile Modal */}
      <ReconcileModal
        isOpen={reconcileModalOpen}
        onClose={() => {
          setReconcileModalOpen(false);
          setOperationToReconcile(null);
        }}
        onConfirm={handleReconcileConfirm}
        operationDescription={operationToReconcile?.counterparty?.name || operationToReconcile?.comment}
      />
      
      {/* Duplicate Modal */}
      <DuplicateModal
        isOpen={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false);
          setOperationToDuplicate(null);
        }}
        onConfirm={handleDuplicateConfirm}
        operation={operationToDuplicate}
      />
    </div>
  );
};

export default OperationsPage;
